import type { AppConfig, GaugeConfigEntry, MqttConfig, ServerConfig } from "../types/config";
import type { AlertRule } from "../types/alerts";

export const defaultGaugeConfig: GaugeConfigEntry[] = [
  {
    id: "rpm",
    topic: "lemons/RPM",
    label: "RPM",
    min: 0,
    max: 8000,
    unit: "rpm",
    zones: [
      { from: 0, to: 5000, color: "#4ade80" },
      { from: 5000, to: 6500, color: "#facc15" },
      { from: 6500, to: 8000, color: "#ef4444" },
    ],
  },
  {
    id: "coolant",
    topic: "lemons/COOLANT_TEMP",
    label: "Coolant",
    min: 0,
    max: 130,
    unit: "\u00b0C",
    zones: [
      { from: 0, to: 50, color: "#60a5fa" },
      { from: 50, to: 100, color: "#4ade80" },
      { from: 100, to: 115, color: "#facc15" },
      { from: 115, to: 130, color: "#ef4444" },
    ],
  },
  {
    id: "afr",
    topic: "lemons/AFR",
    label: "AFR",
    min: 10,
    max: 20,
    unit: "afr",
    decimals: 1,
    zones: [
      { from: 10, to: 12, color: "#ef4444" },
      { from: 12, to: 13.5, color: "#facc15" },
      { from: 13.5, to: 15.2, color: "#4ade80" },
      { from: 15.2, to: 17, color: "#facc15" },
      { from: 17, to: 20, color: "#ef4444" },
    ],
  },
  {
    id: "iat",
    topic: "lemons/INTAKE_TEMP",
    label: "IAT",
    min: 0,
    max: 120,
    unit: "\u00b0C",
    zones: [
      { from: 0, to: 40, color: "#60a5fa" },
      { from: 40, to: 80, color: "#4ade80" },
      { from: 80, to: 100, color: "#facc15" },
      { from: 100, to: 120, color: "#ef4444" },
    ],
  },
  {
    id: "fuel",
    topic: "lemons/fuel/fuel_level",
    label: "Fuel",
    min: 0,
    max: 100,
    unit: "%",
    zones: [
      { from: 0, to: 20, color: "#ef4444" },
      { from: 20, to: 40, color: "#facc15" },
      { from: 40, to: 100, color: "#4ade80" },
    ],
  },
];

export const defaultAlertRules: AlertRule[] = [
  {
    id: "control_module_voltage_low",
    name: "Battery Voltage Low",
    metric: "CONTROL_MODULE_VOLTAGE",
    enabled: true,
    condition: { type: "threshold", op: "lt", value: 12 },
    severity: "warning",
    cooldownMs: 300_000,
    message: "Battery voltage below 12.0 V for an extended period",
  },
  {
    id: "car_pi_cpu_temp_high",
    name: "Car-Pi CPU Temperature High",
    metric: "car-pi/cpu_temp_c",
    enabled: true,
    condition: { type: "threshold", op: "gt", value: 85 },
    severity: "warning",
    cooldownMs: 300_000,
    message: "Car-Pi CPU temperature above 85 °C",
  },
];

export const defaultMqttConfig: MqttConfig = {
  url: import.meta.env.VITE_MQTT_URL ?? `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/mqtt`,
  topicPrefix: "lemons/",
};

export const defaultServerConfig: ServerConfig = {
  url: import.meta.env.VITE_SERVER_URL ?? `${location.protocol}//${location.host}`,
};

export const DEFAULT_TARGET_STINT_MINUTES = 120;

export const defaultAppConfig: AppConfig = {
  mqtt: defaultMqttConfig,
  server: defaultServerConfig,
  gauges: defaultGaugeConfig,
  calibration: [
    {
      topic: "lemons/gps/speed",
      offset: 0,
      scale: 2.23694,
      clampMin: 0,
      clampMax: 200,
    },
  ],
  finishLine: null,
  sectorLines: [],
  alerts: defaultAlertRules,
  targetStintMinutes: DEFAULT_TARGET_STINT_MINUTES,
};
